export interface CustomField {
  label: string;
  example: string;
}

export const TEMPLATE_LIMITS = {
  headerText: 60,
  footerText: 60,
  buttonText: 25,
  maxButtons: 3,
} as const;

export const TEMPLATE_NAME_RE = /^[a-z][a-z0-9_]{0,511}$/;

/** Normaliza nome de template para o formato exigido pela Meta (API WhatsApp Cloud). */
export function normalizeTemplateNameInput(raw: string): string {
  let s = raw
    .normalize("NFD")
    .replace(/\p{M}/gu, "")
    .toLowerCase()
    .replace(/[\s-]+/g, "_")
    .replace(/[^a-z0-9_]/g, "")
    .replace(/_+/g, "_");

  if (s && !/^[a-z]/.test(s)) {
    s = s.replace(/^[^a-z]+/, "");
  }

  return s.slice(0, 512);
}

const CAMPO_TOKEN_RE = /\[Campo\s+(\d+)\]/gi;

export function displayToMeta(text: string): string {
  return text.replace(CAMPO_TOKEN_RE, (_, num) => `{{${num}}}`);
}

export function metaToDisplay(text: string): string {
  return text.replace(/\{\{\s*(\d+)\s*\}\}/g, (_, num) => `[Campo ${num}]`);
}

/** Números de campo na ordem da primeira aparição no texto. */
export function getUsedFieldNumbers(text: string): number[] {
  const order: number[] = [];
  const seen = new Set<number>();
  for (const match of text.matchAll(CAMPO_TOKEN_RE)) {
    const fieldNum = parseInt(match[1], 10);
    if (!seen.has(fieldNum)) {
      seen.add(fieldNum);
      order.push(fieldNum);
    }
  }
  return order;
}

/**
 * A Meta numera variáveis por componente (cabeçalho e corpo são independentes).
 * Cada componente deve usar {{1}}, {{2}}… em sequência, sem lacunas.
 * O formulário usa [Campo N] global — aqui renumeramos por componente na hora do envio.
 */
export function normalizeComponentVariables(
  displayText: string,
  fields: CustomField[],
): { metaText: string; samples: string[] } {
  const fieldOrder = getUsedFieldNumbers(displayText);
  const fieldToMeta = new Map(fieldOrder.map((fieldNum, idx) => [fieldNum, idx + 1]));
  const samples = fieldOrder.map((fieldNum) => fields[fieldNum - 1]?.example?.trim() || "exemplo");

  const metaText = displayText.replace(CAMPO_TOKEN_RE, (_, numStr) => {
    const fieldNum = parseInt(numStr, 10);
    const metaIndex = fieldToMeta.get(fieldNum);
    return metaIndex ? `{{${metaIndex}}}` : `{{${fieldNum}}}`;
  });

  return { metaText, samples };
}

export function substituteVariablesForPreview(text: string, fields: CustomField[]): string {
  return text.replace(CAMPO_TOKEN_RE, (_, numStr) => {
    const index = parseInt(numStr, 10) - 1;
    const example = fields[index]?.example?.trim();
    return example || `[Campo ${numStr}]`;
  });
}

function buildBodyComponent(text: string, fields: CustomField[]): Record<string, unknown> {
  const { metaText, samples } = normalizeComponentVariables(text, fields);
  const fieldOrder = getUsedFieldNumbers(text);
  const comp: Record<string, unknown> = { type: "BODY", text: metaText };
  if (samples.length > 0) {
    comp.example = { body_text: [samples] };
    comp.variable_labels = fieldOrder.map(
      (fieldNum) => fields[fieldNum - 1]?.label?.trim() || `Informação ${fieldNum}`,
    );
  }
  return comp;
}

function buildHeaderComponent(text: string, fields: CustomField[]): Record<string, unknown> {
  const { metaText, samples } = normalizeComponentVariables(text, fields);
  const fieldOrder = getUsedFieldNumbers(text);
  const comp: Record<string, unknown> = {
    type: "HEADER",
    format: "TEXT",
    text: metaText,
  };
  if (samples.length > 0) {
    comp.example = { header_text: [samples[0]] };
    comp.variable_labels = fieldOrder.map(
      (fieldNum) => fields[fieldNum - 1]?.label?.trim() || `Cabeçalho ${fieldNum}`,
    );
  }
  return comp;
}

export interface TemplateFormInput {
  headerEnabled: boolean;
  headerText: string;
  bodyText: string;
  footerText: string;
  buttons: string[];
  customFields: CustomField[];
}

export function buildTemplateComponents(input: TemplateFormInput): Record<string, unknown>[] {
  const components: Record<string, unknown>[] = [];

  if (input.headerEnabled && input.headerText.trim()) {
    components.push(buildHeaderComponent(input.headerText.trim(), input.customFields));
  }

  components.push(buildBodyComponent(input.bodyText.trim(), input.customFields));

  if (input.footerText.trim()) {
    components.push({ type: "FOOTER", text: input.footerText.trim() });
  }

  const validButtons = input.buttons.map((b) => b.trim()).filter(Boolean).slice(0, TEMPLATE_LIMITS.maxButtons);
  if (validButtons.length > 0) {
    components.push({
      type: "BUTTONS",
      buttons: validButtons.map((text) => ({ type: "QUICK_REPLY", text })),
    });
  }

  return components;
}

export interface TemplateFormValidation {
  ok: boolean;
  title?: string;
  description?: string;
}

function startsOrEndsWithVariable(text: string): boolean {
  const trimmed = text.trim();
  return /^\[Campo\s+\d+\]/i.test(trimmed) || /\[Campo\s+\d+\]$/i.test(trimmed);
}

function hasAdjacentVariables(text: string): boolean {
  return /\[Campo\s+\d+\]\s*\[Campo\s+\d+\]/i.test(text);
}

function validateComponentVariables(
  label: string,
  text: string,
  fields: CustomField[],
  options?: { maxVariables?: number },
): TemplateFormValidation | null {
  const used = getUsedFieldNumbers(text);
  if (options?.maxVariables !== undefined && used.length > options.maxVariables) {
    return {
      ok: false,
      title: "Variáveis demais",
      description:
        options.maxVariables === 1
          ? `O ${label} aceita no máximo uma variável.`
          : `O ${label} aceita no máximo ${options.maxVariables} variáveis.`,
    };
  }

  if (startsOrEndsWithVariable(text)) {
    return {
      ok: false,
      title: "Posição da variável inválida",
      description: `O ${label} não pode começar nem terminar com um campo personalizado.`,
    };
  }

  if (hasAdjacentVariables(text)) {
    return {
      ok: false,
      title: "Variáveis adjacentes",
      description: `Separe os campos personalizados com texto no ${label}.`,
    };
  }

  const missingExample = used.find((fieldNum) => !fields[fieldNum - 1]?.example?.trim());
  if (missingExample) {
    const label = fields[missingExample - 1]?.label?.trim() || `campo ${missingExample}`;
    return {
      ok: false,
      title: "Exemplo obrigatório",
      description: `Preencha um exemplo para "${label}" — a Meta usa isso na revisão do template.`,
    };
  }

  return null;
}

export function validateTemplateForm(
  name: string,
  input: TemplateFormInput,
  options?: { buttonsAllowed?: boolean },
): TemplateFormValidation {
  const trimmedName = name.trim().toLowerCase();
  if (!TEMPLATE_NAME_RE.test(trimmedName)) {
    return {
      ok: false,
      title: "Nome inválido",
      description: "Use apenas letras minúsculas, números e underscore (ex.: boas_vindas_pedido).",
    };
  }

  if (!input.bodyText.trim()) {
    return { ok: false, title: "Corpo obrigatório", description: "Informe o texto do corpo da mensagem." };
  }

  if (input.headerEnabled && input.headerText.trim().length > TEMPLATE_LIMITS.headerText) {
    return {
      ok: false,
      title: "Cabeçalho muito longo",
      description: `O cabeçalho pode ter no máximo ${TEMPLATE_LIMITS.headerText} caracteres.`,
    };
  }

  if (input.footerText.trim().length > TEMPLATE_LIMITS.footerText) {
    return {
      ok: false,
      title: "Rodapé muito longo",
      description: `O rodapé pode ter no máximo ${TEMPLATE_LIMITS.footerText} caracteres.`,
    };
  }

  const invalidButton = input.buttons.find((b) => b.trim().length > TEMPLATE_LIMITS.buttonText);
  if (invalidButton) {
    return {
      ok: false,
      title: "Botão muito longo",
      description: `Cada botão pode ter no máximo ${TEMPLATE_LIMITS.buttonText} caracteres.`,
    };
  }

  const filledButtons = input.buttons.map((b) => b.trim()).filter(Boolean);
  if (!options?.buttonsAllowed && filledButtons.length > 0) {
    return {
      ok: false,
      title: "Botões indisponíveis",
      description: "Botões de resposta rápida estão disponíveis apenas para Marketing e Utilidade.",
    };
  }

  if (input.headerEnabled && input.headerText.trim()) {
    const headerError = validateComponentVariables("cabeçalho", input.headerText, input.customFields, {
      maxVariables: 1,
    });
    if (headerError) return headerError;
  }

  const bodyError = validateComponentVariables("corpo", input.bodyText, input.customFields);
  if (bodyError) return bodyError;

  return { ok: true };
}
